import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../../services/api';
import AuthorizationsTable from './AuthorizationsTable';
import RetentionPanel from './RetentionPanel';
import './HealthPanel.css';

const RANGES = [7, 30, 90];

/* One figure on the stats sheet. The kit dropped its StatCard (0.317); this is
   the same tile in Nucleus's stat voice: the value, its label, an optional
   line under it (red only when the tone says the number is bad). */
function StatCard({ value, label, delta, deltaTone }) {
  return (
    <div className="health__stat">
      <span className="health__stat-label">{label}</span>
      <span className="health__stat-value">{value}</span>
      {delta && <span className={`health__stat-delta${deltaTone === 'down' ? ' is-down' : ''}`}>{delta}</span>}
    </div>
  );
}

/**
 * How an app is actually behaving, on the app's own page.
 *
 * The metrics endpoint has always returned error rate and latency —
 * `status_codes` and `avg_response_time_ms` — and nothing in the portal has ever
 * displayed either. Everything here is read from data the backend already has.
 */
/* Three questions, three views. They were one scroll: headline numbers, a chart,
 * two breakdown tables and a collapsed list of people — read top to bottom
 * whether you came for "is it being used" or "who is using it". */
const VIEWS = [
  { key: 'overview', label: 'Overview' },
  { key: 'authorizations', label: 'User Authorizations' },
  { key: 'retention', label: 'Retention' },
  { key: 'analytics', label: 'Analytics' },
];

function HealthPanel({ appId, websiteUrl }) {
  const [view, setView] = useState('overview');
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      // Usage only. Whether the app is fit to be here at all is OS-Readiness,
      // which asks a different question on a different timescale.
      setData(await api.request(`/third-party/${appId}/metrics?days=${days}`));
    } catch (err) {
      setError(err.message || 'Could not load metrics.');
    }
    setLoading(false);
  }, [appId, days]);

  useEffect(() => { load(); }, [load]);

  const derived = useMemo(() => {
    if (!data) return null;
    const codes = data.status_codes || [];
    const total = codes.reduce((n, c) => n + c.count, 0);
    const failed = codes.filter(c => c.status_code >= 400).reduce((n, c) => n + c.count, 0);
    const denied = codes.filter(c => c.status_code === 403).reduce((n, c) => n + c.count, 0);
    const byDay = data.calls_by_day || [];
    const peak = byDay.reduce((m, d) => Math.max(m, d.count), 0);
    return {
      errorRate: total ? (failed / total) * 100 : null,
      failed,
      denied,
      byDay,
      peak,
      quiet: byDay.length === 0,
    };
  }, [data]);

  if (loading && !data) return <p className="health__msg">Loading…</p>;
  if (error) return <p className="health__msg health__msg--bad">{error}</p>;

  const s = data?.summary || {};
  const latency = s.avg_response_time_ms;

  return (
    <div className="health">
      <div className="tabstrip health__views" role="tablist" aria-label="Usage metrics views">
        {VIEWS.map(v => (
          <button
            key={v.key}
            role="tab"
            aria-selected={view === v.key}
            className="tabstrip__tab"
            onClick={() => setView(v.key)}
          >
            {v.label}
          </button>
        ))}
      </div>

      {view === 'authorizations' ? (
        <AuthorizationsTable appId={appId} />
      ) : view === 'retention' ? (
        <RetentionPanel appId={appId} />
      ) : (
      <>
      <div className="health__head">
        {/* The range is about calls over time, so it belongs to the two views
            that are about calls over time. */}
        <div className="health__range" role="group" aria-label="Time range">
          {RANGES.map(d => (
            <button
              key={d}
              className={`health__range-btn ${d === days ? 'is-active' : ''}`}
              onClick={() => setDays(d)}
            >
              {d}d
            </button>
          ))}
        </div>
        {websiteUrl && (
          <a className="health__link" href={websiteUrl} target="_blank" rel="noopener noreferrer">
            Open the app ↗
          </a>
        )}
      </div>

      {view === 'overview' && (<>
      <div className="health__stats">
        <StatCard value={fmt(s.total_calls)} label="API calls" />
        <StatCard value={fmt(s.unique_users)} label="People using it" />
        <StatCard
          value={derived.errorRate === null ? '—' : `${derived.errorRate.toFixed(1)}%`}
          label="Failed calls"
          delta={derived.failed ? `${fmt(derived.failed)} of ${fmt(s.total_calls)}` : null}
          deltaTone={derived.errorRate > 5 ? 'down' : 'flat'}
        />
        <StatCard
          value={latency == null ? '—' : `${Math.round(latency)}ms`}
          label="Typical response"
          deltaTone="flat"
        />
      </div>

      {derived.denied > 0 && (
        <p className="health__denied">
          <strong>{fmt(derived.denied)}</strong> {derived.denied === 1 ? 'call was' : 'calls were'} refused
          for permission. That usually means the app is asking for something people did not grant —
          worth comparing against what it requests under Access.
        </p>
      )}


      <section className="health__block">
        <h4>Calls per day</h4>
        {derived.quiet ? (
          <p className="health__empty">
            Nothing in the last {days} days. Either nobody has used it, or it is not reachable.
          </p>
        ) : (
          <div className="health__chart" role="img" aria-label={`Calls per day over ${days} days`}>
            {derived.byDay.map(d => (
              <div key={d.date} className="health__bar-wrap" title={`${d.date}: ${d.count}`}>
                <div
                  className="health__bar"
                  style={{ height: `${derived.peak ? (d.count / derived.peak) * 100 : 0}%` }}
                />
              </div>
            ))}
          </div>
        )}
        {!derived.quiet && (
          <div className="health__axis">
            <span>{derived.byDay[0]?.date}</span>
            <span>peak {fmt(derived.peak)}/day</span>
            <span>{derived.byDay[derived.byDay.length - 1]?.date}</span>
          </div>
        )}
      </section>

      </>)}

      {view === 'analytics' && (
      <div className="health__cols">
        <section className="health__block">
          <h4>Busiest endpoints</h4>
          {(data.calls_by_endpoint || []).length === 0 ? (
            <p className="health__empty">No calls yet.</p>
          ) : (
            <table className="health__table">
              <tbody>
                {data.calls_by_endpoint.slice(0, 8).map((e, i) => (
                  <tr key={i}>
                    <td className="health__method">{e.method}</td>
                    <td className="health__endpoint">{e.endpoint}</td>
                    <td className="health__num">{fmt(e.count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>

        <section className="health__block">
          <h4>Most active people</h4>
          {(data.top_users || []).length === 0 ? (
            <p className="health__empty">Nobody has used it yet.</p>
          ) : (
            <table className="health__table">
              <tbody>
                {data.top_users.slice(0, 8).map(u => (
                  <tr key={u.user_id}>
                    <td className="health__endpoint">{u.username}</td>
                    <td className="health__num">{fmt(u.calls)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </section>
      </div>
      )}
      </>
      )}
    </div>
  );
}

function fmt(n) {
  if (n == null) return '—';
  if (n >= 1000000) return `${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `${(n / 1000).toFixed(1)}k`;
  return String(n);
}

export default HealthPanel;
